import { computed, Injectable, signal } from "@angular/core";
import { OrganizationMicroService } from "../../services/organization.micro.service";
import { S25ItemI } from "../../pojo/S25ItemI";
import { StudentEngagementOrg } from "./StudentEngagementOrg";
import { CustomAttributes } from "../../pojo/CustomAttributes";
import { MicroUtil } from "../../services/MicroServices/micro.util";
import { S25Util } from "../../util/s25-util";
import { ContactService } from "../../services/contact.service";
import { EventService } from "../../services/event.service";
import { EventI } from "../../pojo/EventI";
import { Proto } from "../../pojo/Proto";
import SeOrg = StudentEngagementOrg.SeOrg;
import { S25WsMicroOrganization } from "../../services/MicroServices/S25WsMicroOrganization";
import { MasterDefinitionService } from "../../services/master.definitions/master.definition.service";
import { SeEvent } from "./event/se.event.details.view.component";
import { EventMicroService } from "../../services/event.micro.service";
import { UserprefService } from "../../services/userpref.service";
import { Space, WSReservations } from "../s25-event/EventMicroI";
import { StudentEngagementUtil } from "./student.engagement.util";

export const enum UserType {
    ADMIN = -1,
    OFFICER = 43,
}

@Injectable()
export class StudentEngagementService {
    private _groupId = signal<number | null>(null); // placeholder for edit rights logic

    groupId = this._groupId.asReadonly();

    setGroupId(id: number) {
        this._groupId.set(id);
    }

    hasEditRights = computed(() => [UserType.ADMIN, UserType.OFFICER].includes(this._groupId()));

    public static async getOrganizationDetails(itemId: number): Promise<StudentEngagementOrg.details> {
        const [organization, upcomingEvents] = await Promise.all([
            OrganizationMicroService.getOrganizationIncludes([itemId], ["contacts", "attributes"], true),
            StudentEngagementService.getUpcomingEventsByOrg(itemId),
        ]);
        const details = organization.data.items[0];
        const expandedInfo = organization.expandedInfo[0];
        const mission =
            details.attributes.find(
                (attr) => attr.attributeId === CustomAttributes.system.StudentEngagementOrgMission.id,
            )?.value || "No mission statement provided";

        const membersArr = S25Util.array
            .forceArray(expandedInfo.contacts)
            .filter((cont) => !!details.contacts.find((c) => c.contactId === cont.contactId))
            .map((cont) => {
                const contactDetails = MicroUtil.getContactDetails(cont.contactId, [cont]);
                return { ...contactDetails, itemId: cont.contactId, itemName: contactDetails.fullName };
            });
        const members = signal<S25ItemI[]>(membersArr);
        const primaryContact = membersArr[0]; //TODO: implement primary contact logic

        return {
            name: details.organizationName,
            mission: mission,
            primaryContact: primaryContact,
            members: members,
            imageAttr: details.attributes.find(
                (attr) => attr.attributeId === CustomAttributes.system.StudentEngagementOrgLogo.id,
            ),
            upcomingEvents: upcomingEvents,
        };
    }

    /*
    Fetches all the upcoming events where the current user is part of the sponsoring organization
    */
    public static async getMyUpcomingEvents(): Promise<SeEvent[]> {
        const orgs = await ContactService.getCurrentAssociatedOrganizations();
        const eventsResp = await EventService.getEventsBySearchQuery(
            `node_type=E&order=asc&organization_id=${orgs.map((org) => org.organization_id).join("+")}&ends_after=${S25Util.date.toS25ISODateStrStartOfDay(new Date())}&paginate&page_size=10`,
        );
        if (eventsResp?.length) {
            const [events] = await S25Util.Maybe(
                EventMicroService.getEventDetailById(
                    eventsResp.map((event) => event.event_id),
                    "F",
                    "all",
                ),
            );
            if (events) {
                const evDetails = events?.content?.data?.items ?? [],
                    expandedInfo = events?.content?.expandedInfo ?? {};

                return evDetails
                    .map((e) => {
                        return {
                            itemId: e.id,
                            itemName: e.name,
                            date: e.dates.startDate,
                            location: expandedInfo.spaces?.[0]?.spaceName,
                            imageAttr: e.attributes?.find(
                                (attr) => attr.attributeId === CustomAttributes.system.HotEventImage.id,
                            ),
                        };
                    })
                    .sort((a, b) => +new Date(b.date) - +new Date(a.date));
            }
        }
        return [];
    }

    public static async getUpcomingEventsByOrg(orgId: number) {
        const [events, error] = await S25Util.Maybe(EventService.getUpcomingEventsByOrg(orgId));
        if (error) {
            console.error(error);
            return [];
        }
        return events
            .sort((a, b) => +new Date(b.start_date) - +new Date(a.start_date))
            .map((event: EventI) => ({
                itemId: event.event_id,
                itemName: event.event_name,
                date: event.start_date,
                description: "Description goes here",
                location: "Location goes here",
            }));
    }

    public static async getAllUpcomingEvents(): Promise<SeEvent[]> {
        return StudentEngagementService.getUpcomingEventsByOrg(1234); // Placeholder for general events GET
    }

    public static async getMyOrgs(): Promise<SeOrg[]> {
        const myOrgs = await ContactService.getCurrentAssociatedOrganizations();
        if (myOrgs?.length) {
            const orgData = await OrganizationMicroService.getOrganizationIncludes(
                    myOrgs?.map((org) => org.organization_id) ?? [],
                    ["attributes"],
                    false,
                ),
                orgMap = StudentEngagementService.getLogoUrlMap(orgData?.data?.items ?? []);
            return myOrgs.map((org) => ({
                itemId: org.organization_id,
                itemName: org.organization_name,
                logoUrl: orgMap[org.organization_id],
            }));
        }
        return [];
    }

    public static async getAllOrgs() {
        // Todo: getUsage requires auth, so would need to enable without auth or find another way to get those organizations
        const seOrgs = await MasterDefinitionService.getUsage(
                "org_custom_attribute",
                CustomAttributes.system.StudentEngagementOrg.id,
            ),
            orgData = await OrganizationMicroService.getOrganizationIncludes(
                seOrgs?.object?.map((obj) => +obj.object_id) ?? [],
                ["attributes"],
            ),
            orgMap = StudentEngagementService.getLogoUrlMap(orgData?.data?.items ?? []);
        return (
            orgData?.data?.items?.map((org) => ({
                itemId: org.id,
                itemName: org.organizationName,
                logoUrl: orgMap[org.id],
            })) ?? []
        );
    }

    private static getLogoUrlMap(orgs: S25WsMicroOrganization.Item[]) {
        return (
            orgs?.reduce((map: Record<number, Proto.URLString>, org) => {
                const logoAttr = org.attributes?.find(
                    (attr) => attr.attributeId === CustomAttributes.system.StudentEngagementOrgLogo.id,
                );
                if (logoAttr) map[org.id] = StudentEngagementUtil.parseUrl(logoAttr.value);
                return map;
            }, {}) ?? {}
        );
    }

    public static async getTasks(): Promise<S25ItemI[]> {
        return [];
    }

    public static async getEventDetails(eventId: number) {
        const [event, datePrefResp, dateTimePrefResp, timePrefResp] = await Promise.allSettled([
            EventMicroService.getEventDetailById([eventId], "F", "all"),
            UserprefService.getS25Dateformat(),
            UserprefService.getS25DateTimeformat(),
            UserprefService.getS25Timeformat(),
        ]);
        const data = event.status === "fulfilled" && event.value?.content,
            details = data?.data?.items?.[0],
            expandedDetails = data?.expandedInfo ?? {},
            datePref = datePrefResp.status === "fulfilled" && datePrefResp.value,
            dateTimePref = dateTimePrefResp.status === "fulfilled" && dateTimePrefResp.value,
            timePref = timePrefResp.status === "fulfilled" && timePrefResp.value,
            datePrefs = { date: datePref, dateTime: dateTimePref, time: timePref },
            spaceMap = expandedDetails.spaces?.reduce(
                (map: Record<number, string>, space) => (
                    (map[space.spaceId] =
                        `${space.spaceName}${space.spaceFormalName ? ` - ${space.spaceFormalName}` : ""}`),
                    map
                ),
                {},
            ),
            occurrences = details.profiles?.flatMap((profile) =>
                profile.reservations
                    ?.sort(
                        (a: WSReservations, b: WSReservations) => +new Date(a.rsrvStartDt) - +new Date(b.rsrvStartDt),
                    )
                    ?.map((res: WSReservations) => ({
                        date: StudentEngagementUtil.formatDate(
                            <string>res.rsrvStartDt,
                            <string>res.rsrvEndDt,
                            datePrefs,
                        ),
                        locations:
                            res.spaces?.flatMap((space) =>
                                Object.values(space)
                                    .flat()
                                    .map((space: Space) => spaceMap[space.spaceId] ?? ""),
                            ) ?? [],
                    })),
            );
        if (details) {
            const hostOrgId = S25Util.array.forceArray(details.organizations).find((org) => !!org.primary)?.primary;
            let hostOrg;
            if (hostOrgId) {
                const [org] = await S25Util.Maybe(
                    OrganizationMicroService.getOrganizationIncludes([hostOrgId], ["attributes"], false),
                );
                if (org) {
                    const orgItem = org.data?.items?.[0];
                    const logoUrl = orgItem?.attributes?.find(
                        (attr) => attr.attributeId === CustomAttributes.system.StudentEngagementOrgLogo.id,
                    )?.value;
                    hostOrg = {
                        itemId: hostOrgId,
                        itemName: orgItem?.organizationName,
                        logoUrl: StudentEngagementUtil.parseUrl(logoUrl),
                    };
                }
            }
            return {
                itemName: details.name,
                date: StudentEngagementUtil.formatDate(details.dates.startDate, details.dates.endDate, datePrefs),
                locations:
                    expandedDetails?.spaces?.map(
                        (space) => `${space.spaceName}${space.spaceFormalName ? ` - ${space.spaceFormalName}` : ""}`,
                    ) ?? [],
                hostOrg: hostOrg,
                occurrences: occurrences,
                imageAttr: S25Util.array
                    .forceArray(details.attributes)
                    .find((attr) => attr.attributeId === CustomAttributes.system.HotEventImage.id),
            };
        }
    }
}
